/**
 * ListPickerModal Component
 *
 * Modal for selecting which lists to save a property to.
 * Opens when user clicks heart icon on a property card.
 * Shows all user lists with checkboxes and inline list creation.
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../base/Modal';
import { useFavorites } from '../../hooks/useFavorites';
import { useToast } from '../common/ToastProvider';

export interface ListPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  propertyId: string;
  propertyTitle?: string;
  propertyImageUrl?: string | null;
  onSaved?: () => void;
}

export default function ListPickerModal({
  isOpen,
  onClose,
  propertyId,
  propertyTitle,
  propertyImageUrl,
  onSaved,
}: ListPickerModalProps) {
  const { t } = useTranslation('favorites');
  const { lists, isPropertyInList, copyToList, removeFavorite, createList, addFavorite, isFavorite } = useFavorites();
  const { showToast } = useToast();

  const [loadingListId, setLoadingListId] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const handleToggleList = useCallback(async (listId: string, isInList: boolean) => {
    setLoadingListId(listId);
    try {
      if (isInList) {
        // Remove from this specific list
        await removeFavorite(propertyId, listId);
        showToast(t('favorites.listPicker.removedFromList'), 'success');
      } else {
        // Add to list
        // If property is not in favorites at all, add it first
        if (!isFavorite(propertyId)) {
          await addFavorite(propertyId, listId);
        } else {
          await copyToList(propertyId, listId);
        }
        showToast(t('favorites.listPicker.addedToList'), 'success');
      }
      onSaved?.();
    } catch (error) {
      console.error('Failed to update list:', error);
      showToast(t('favorites.listPicker.error'), 'error');
    } finally {
      setLoadingListId(null);
    }
  }, [propertyId, removeFavorite, copyToList, addFavorite, isFavorite, showToast, t, onSaved]);

  const handleCreateList = useCallback(async () => {
    if (!newListName.trim()) return;

    setIsCreating(true);
    try {
      const newList = await createList(newListName.trim());
      // Add property to the newly created list
      if (!isFavorite(propertyId)) {
        await addFavorite(propertyId, newList.id);
      } else {
        await copyToList(propertyId, newList.id);
      }
      showToast(t('favorites.listPicker.listCreated', { name: newListName }), 'success');
      setNewListName('');
      setShowCreateForm(false);
      onSaved?.();
    } catch (error) {
      console.error('Failed to create list:', error);
      showToast(t('favorites.listPicker.createError'), 'error');
    } finally {
      setIsCreating(false);
    }
  }, [newListName, createList, copyToList, addFavorite, isFavorite, propertyId, showToast, t, onSaved]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isCreating) {
      handleCreateList();
    }
  }, [handleCreateList, isCreating]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('favorites.listPicker.title')}
      icon="ri-bookmark-line"
      size="sm"
    >
      <div className="flex flex-col gap-4">
        {/* Property Preview */}
        {(propertyTitle || propertyImageUrl) && (
          <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-[var(--pv-color-surface-soft)] rounded-xl">
            {propertyImageUrl && (
              <img
                src={propertyImageUrl}
                alt={propertyTitle || ''}
                className="w-14 h-14 rounded-lg object-cover flex-shrink-0"
              />
            )}
            {propertyTitle && (
              <p className="text-sm font-medium text-[var(--pv-color-text)] line-clamp-2">
                {propertyTitle}
              </p>
            )}
          </div>
        )}

        {/* Lists */}
        <div className="flex flex-col gap-1 max-h-64 overflow-y-auto">
          {lists.map((list) => {
            const isInList = isPropertyInList(propertyId, list.id);
            const isLoading = loadingListId === list.id;

            return (
              <button
                key={list.id}
                onClick={() => handleToggleList(list.id, isInList)}
                disabled={isLoading}
                className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-[var(--pv-color-surface-soft)] transition-colors group disabled:opacity-60"
              >
                {/* List Icon */}
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${list.color || '#004D40'}20` }}
                >
                  <i
                    className={`${list.icon || 'ri-folder-line'} text-lg`}
                    style={{ color: list.color || '#004D40' }}
                  />
                </div>

                {/* List Info */}
                <div className="flex-1 text-start min-w-0">
                  <p className="text-sm font-medium text-[var(--pv-color-text)] truncate">
                    {list.name}
                  </p>
                  <p className="text-xs text-[var(--pv-color-text-muted)]">
                    {t('favorites.listPicker.propertyCount', { count: list.propertyIds.length })}
                  </p>
                </div>

                {/* Checkbox */}
                <div className="flex-shrink-0">
                  {isLoading ? (
                    <div className="w-5 h-5 border-2 border-[var(--pv-color-primary)] border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <div
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                        isInList
                          ? 'bg-[var(--pv-color-primary)] border-[var(--pv-color-primary)]'
                          : 'border-gray-300 dark:border-gray-600 group-hover:border-[var(--pv-color-primary)]'
                      }`}
                    >
                      {isInList && (
                        <i className="ri-check-line text-white text-sm" />
                      )}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Create New List */}
        {showCreateForm ? (
          <div className="flex flex-col gap-2 pt-2 border-t border-[var(--pv-border-soft)]">
            <input
              type="text"
              value={newListName}
              onChange={(e) => setNewListName(e.target.value)}
              onKeyDown={handleKeyDown}
              onClick={(e) => e.stopPropagation()}
              placeholder={t('favorites.listPicker.newListPlaceholder')}
              disabled={isCreating}
              autoFocus
              className="w-full px-3 py-2 text-sm rounded-lg border border-[var(--pv-border-soft)] bg-white dark:bg-[var(--pv-color-surface-strong)] text-[var(--pv-color-text)] placeholder:text-[var(--pv-color-text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--pv-color-primary)] focus:border-transparent disabled:opacity-60"
            />
            <div className="flex gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowCreateForm(false);
                  setNewListName('');
                }}
                disabled={isCreating}
                className="flex-1 px-3 py-2 text-sm font-medium text-[var(--pv-color-text-muted)] hover:text-[var(--pv-color-text)] rounded-lg hover:bg-gray-100 dark:hover:bg-[var(--pv-color-surface-soft)] transition-colors disabled:opacity-60"
              >
                {t('favorites.listPicker.cancel')}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCreateList();
                }}
                disabled={isCreating || !newListName.trim()}
                className="flex-1 px-3 py-2 text-sm font-medium text-white bg-[var(--pv-color-primary)] hover:bg-[var(--pv-color-primary-dark)] rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isCreating && (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                )}
                {t('favorites.listPicker.create')}
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowCreateForm(true);
            }}
            className="flex items-center gap-2 p-3 rounded-xl text-[var(--pv-color-primary)] hover:bg-[var(--pv-color-primary)]/10 transition-colors"
          >
            <i className="ri-add-line text-lg" />
            <span className="text-sm font-medium">{t('favorites.listPicker.createNew')}</span>
          </button>
        )}
      </div>
    </Modal>
  );
}
